import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { Plus, Trash2, Edit2, Save, X, Mail, Star } from 'lucide-react';
import toast from 'react-hot-toast';

interface StudentEmail {
  id: number;
  value: string;
  is_primary: boolean;
  consent_status: string;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

interface StudentEmailsManagerProps {
  studentUnid: string;
  studentName: string;
}

export default function StudentEmailsManager({ studentUnid, studentName }: StudentEmailsManagerProps) {
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingEmail, setEditingEmail] = useState('');
  const queryClient = useQueryClient();

  // Fetch student emails
  const { data: studentEmails = [], isLoading, error } = useQuery<StudentEmail[]>(
    ['student-emails', studentUnid],
    async () => {
      const response = await api.get(`/contacts/student/${studentUnid}/emails`);
      return response.data.data;
    },
    {
      enabled: !!studentUnid,
      retry: 3,
    }
  );

  // Add new email mutation
  const addEmailMutation = useMutation(
    async (email: string) => {
      const response = await api.post(`/contacts/student/${studentUnid}/email`, { 
        email,
        isPrimary: studentEmails.length === 0 // First email is primary by default
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-emails', studentUnid]);
        setIsAddingEmail(false);
        setNewEmail('');
        toast.success('Schüler-E-Mail-Adresse erfolgreich hinzugefügt');
      },
      onError: (error: any) => {
        console.error('Error adding student email:', error);
        const message = error?.response?.data?.error || 'Fehler beim Hinzufügen der E-Mail-Adresse';
        toast.error(message);
      },
    }
  );

  // Update email mutation
  const updateEmailMutation = useMutation(
    async ({ id, email, isPrimary }: { id: number; email?: string; isPrimary?: boolean }) => {
      const response = await api.put(`/contacts/${id}`, { email, isPrimary });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-emails', studentUnid]);
        setEditingId(null);
        setEditingEmail('');
        toast.success('E-Mail-Adresse erfolgreich aktualisiert');
      },
      onError: (error: any) => {
        console.error('Error updating student email:', error);
        const message = error?.response?.data?.error || 'Fehler beim Aktualisieren der E-Mail-Adresse';
        toast.error(message);
      },
    }
  );

  // Delete email mutation
  const deleteEmailMutation = useMutation(
    async (id: number) => {
      const response = await api.delete(`/contacts/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['student-emails', studentUnid]);
        toast.success('E-Mail-Adresse erfolgreich gelöscht');
      },
      onError: (error: any) => {
        console.error('Error deleting student email:', error);
        const message = error?.response?.data?.error || 'Fehler beim Löschen der E-Mail-Adresse';
        toast.error(message);
      },
    }
  );

  const handleAddEmail = () => {
    if (!newEmail.trim()) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }
    addEmailMutation.mutate(newEmail.trim());
  };

  const handleEditEmail = (email: StudentEmail) => {
    setEditingId(email.id);
    setEditingEmail(email.value);
  };

  const handleSaveEdit = () => {
    if (!editingEmail.trim()) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }
    if (editingId) {
      updateEmailMutation.mutate({ id: editingId, email: editingEmail.trim() });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingEmail('');
  };

  const handleDeleteEmail = (id: number) => {
    if (window.confirm('Möchten Sie diese E-Mail-Adresse wirklich löschen?')) {
      deleteEmailMutation.mutate(id);
    }
  };

  const handleSetPrimary = (id: number) => {
    updateEmailMutation.mutate({ id, isPrimary: true });
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Lade Schüler-E-Mail-Adressen...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-white p-4 dark:border-red-800 dark:bg-gray-900">
        <div className="text-red-600 text-center">
          Fehler beim Laden der Schüler-E-Mail-Adressen
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center text-lg font-semibold text-gray-800 dark:text-gray-100">
          <Mail className="h-5 w-5 mr-2 text-green-600" />
          Schüler-E-Mail-Adressen
        </h3>
        <button
          onClick={() => setIsAddingEmail(true)}
          disabled={isAddingEmail}
          className="btn btn-primary btn-sm flex items-center"
        >
          <Plus className="h-4 w-4 mr-1" />
          Hinzufügen
        </button>
      </div>

      {/* Add new email form */}
      {isAddingEmail && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center space-x-2">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Schüler-E-Mail-Adresse eingeben"
              className="flex-1 input input-sm border-gray-300 focus:border-green-500 focus:ring-green-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddEmail();
                if (e.key === 'Escape') {
                  setIsAddingEmail(false);
                  setNewEmail('');
                }
              }}
              autoFocus
            />
            <button
              onClick={handleAddEmail}
              disabled={addEmailMutation.isLoading}
              className="btn btn-primary btn-sm flex items-center"
            >
              {addEmailMutation.isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={() => {
                setIsAddingEmail(false);
                setNewEmail('');
              }}
              className="btn btn-secondary btn-sm flex items-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Email list */}
      <div className="space-y-2">
        {studentEmails.length === 0 ? (
          <div className="py-6 text-center text-gray-500 dark:text-gray-400">
            <Mail className="mx-auto mb-2 h-8 w-8 text-gray-400 dark:text-gray-500" />
            <p>Keine Schüler-E-Mail-Adressen vorhanden</p>
            <p className="text-sm">Klicken Sie auf "Hinzufügen" um eine E-Mail-Adresse hinzuzufügen</p>
          </div>
        ) : (
          studentEmails.map((email) => (
            <div
              key={email.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                email.is_primary 
                  ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                  : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
              }`}
            >
              {editingId === email.id ? (
                <div className="flex items-center space-x-2 flex-1">
                  <input
                    type="email"
                    value={editingEmail}
                    onChange={(e) => setEditingEmail(e.target.value)}
                    className="flex-1 input input-sm border-gray-300 focus:border-green-500 focus:ring-green-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    autoFocus
                  />
                  <button
                    onClick={handleSaveEdit}
                    disabled={updateEmailMutation.isLoading}
                    className="btn btn-primary btn-sm flex items-center"
                  >
                    {updateEmailMutation.isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn btn-secondary btn-sm flex items-center"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center space-x-2 flex-1">
                    {email.is_primary && (
                      <span title="Primäre E-Mail-Adresse"><Star className="h-4 w-4 text-yellow-500 fill-yellow-500" /></span>
                    )}
                    <span className="font-medium text-gray-800 dark:text-gray-100">{email.value}</span>
                    {email.consent_status === 'revoked' && (
                      <span className="rounded bg-red-100 px-2 py-1 text-xs text-red-600 dark:bg-red-900/30 dark:text-red-200">Widerrufen</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {!email.is_primary && (
                      <button
                        onClick={() => handleSetPrimary(email.id)}
                        className="btn btn-secondary btn-sm flex items-center"
                        title="Als primäre E-Mail-Adresse setzen"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEditEmail(email)}
                      className="btn btn-secondary btn-sm flex items-center"
                      title="Bearbeiten"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteEmail(email.id)}
                      className="btn btn-danger btn-sm flex items-center"
                      title="Löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
